interface OrdonnanceDoc {
  id: string;
  ordonnance: string;
  duree?: string;
  instructions?: string;
  date: string;
}

interface BonLaboDoc {
  id: string;
  analyses: string[];
  urgences: boolean;
  observations?: string;
  date: string;
}

interface ImprimerDocumentsSectionProps {
  hasFicheSoin: boolean;
  soinsCount: number;
  ordonnances: OrdonnanceDoc[];
  bonsLabo: BonLaboDoc[];
  onPrintFicheSoin: () => void;
  onPrintOrdonnance: (o: OrdonnanceDoc) => void;
  onPrintBon: (b: BonLaboDoc) => void;
}

export default function ImprimerDocumentsSection({
  hasFicheSoin,
  soinsCount,
  ordonnances,
  bonsLabo,
  onPrintFicheSoin,
  onPrintOrdonnance,
  onPrintBon,
}: ImprimerDocumentsSectionProps) {
  const hasAny = hasFicheSoin || ordonnances.length > 0 || bonsLabo.length > 0;

  return (
    <section className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="p-6 border-b border-gray-100">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    Imprimer les documents
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Imprimez chaque document séparément (fiche de soin, ordonnance de sortie, bon de laboratoire).
        </p>
      </div>

      <div className="p-6 space-y-4">
        {!hasAny && (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto bg-gray-100 rounded-full flex items-center justify-center">
              <i className="ri-file-list-3-line text-2xl text-gray-400"></i>
            </div>
            <p className="text-sm text-gray-500 mt-3 font-medium">Aucun document à imprimer</p>
            <p className="text-xs text-gray-400 mt-1">
              Ajoutez des soins, une ordonnance de sortie ou un bon de laboratoire pour pouvoir les imprimer.
            </p>
          </div>
        )}

        {/* Fiche de soin */}
        {hasFicheSoin && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div>
                <h3 className="font-bold text-gray-900 text-sm">Fiche de Soins</h3>
                <p className="text-xs text-gray-500">
                  {soinsCount} soin{soinsCount > 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <button
              onClick={onPrintFicheSoin}
              className="px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all font-semibold text-sm cursor-pointer whitespace-nowrap flex items-center gap-2 flex-shrink-0"
            >
              <i className="ri-printer-line"></i>
              Imprimer
            </button>
          </div>
        )}

        {/* Ordonnances de sortie */}
        {ordonnances.map((o) => (
          <div
            key={o.id}
            className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          >
            <div className="flex items-center gap-3">
              <div>
                <h3 className="font-bold text-gray-900 text-sm">Ordonnance de sortie</h3>
                <p className="text-xs text-gray-500">
                  {o.date ? new Date(o.date).toLocaleDateString('fr-FR') : ''}
                  {o.duree ? ` · ${o.duree}` : ''}
                </p>
              </div>
            </div>
            <button
              onClick={() => onPrintOrdonnance(o)}
              className="px-5 py-2.5 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-all font-semibold text-sm cursor-pointer whitespace-nowrap flex items-center gap-2 flex-shrink-0"
            >
              <i className="ri-printer-line"></i>
              Imprimer
            </button>
          </div>
        ))}

        {/* Bons de laboratoire */}
        {bonsLabo.map((b) => (
          <div
            key={b.id}
            className="bg-clinic-blue/10 border border-clinic-blue/30 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          >
            <div className="flex items-center gap-3">
              <div>
                <h3 className="font-bold text-gray-900 text-sm">Bon de laboratoire</h3>
                <p className="text-xs text-gray-500">
                  {b.analyses.length} analyse{b.analyses.length > 1 ? 's' : ''}
                  {b.urgences ? ' · URGENT' : ''}
                </p>
              </div>
            </div>
            <button
              onClick={() => onPrintBon(b)}
              className="px-5 py-2.5 bg-clinic-blue text-white rounded-xl hover:bg-clinic-blue-dark transition-all font-semibold text-sm cursor-pointer whitespace-nowrap flex items-center gap-2 flex-shrink-0"
            >
              <i className="ri-printer-line"></i>
              Imprimer
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}